import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clients, industries } from '../../mocks/clients';
import SEO from '../../components/SEO';

export default function ClientsPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [serviceAreasOpen, setServiceAreasOpen] = useState(false);
  const [mobileServiceAreasOpen, setMobileServiceAreasOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredClients = activeFilter === 'All'
    ? clients
    : clients.filter(c => c.industry === activeFilter);

  const serviceAreas = [
    { name: 'Toronto', path: '/service-areas/toronto' },
    { name: 'Mississauga', path: '/service-areas/mississauga' },
    { name: 'Brampton', path: '/service-areas/brampton' },
    { name: 'Oakville', path: '/service-areas/oakville' },
    { name: 'Hamilton', path: '/service-areas/hamilton' },
    { name: 'Richmond Hill', path: '/service-areas/richmond-hill' },
    { name: 'Vaughan', path: '/service-areas/vaughan' },
  ];

  const testimonials = [
    {
      quote: 'Their security system has been flawless for over 5 years. The team is always responsive and professional.',
      name: 'Operations Manager',
      company: 'National Retail Chain',
    },
    {
      quote: 'We switched from our previous provider and the difference in service quality is night and day. Highly recommend.',
      name: 'Facility Director',
      company: 'Hospitality Group',
    },
    {
      quote: 'The access control system they installed streamlined our entire building security. Outstanding work.',
      name: 'Property Manager',
      company: 'Commercial Real Estate',
    },
  ];

  const industryStats = [
    { icon: 'ri-store-2-line', label: 'Retail & Commercial', count: '12,000+' },
    { icon: 'ri-hotel-line', label: 'Hospitality', count: '3,500+' },
    { icon: 'ri-home-4-line', label: 'Residential', count: '10,000+' },
    { icon: 'ri-building-2-line', label: 'Industrial', count: '4,500+' },
  ];

  // Breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://alliancesecurity.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Our Clients",
        "item": "https://alliancesecurity.com/clients"
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Our Clients | Trusted by 30,000+ Businesses | Alliance Security Systems"
        description="Alliance Security Systems protects over 30,000 businesses and homes across the GTA. From local shops to national brands, see why industry leaders trust our security solutions since 1999."
        keywords="security clients, business security, commercial security, retail security, hospitality security, industrial security, trusted security company"
        canonicalUrl="/clients"
        structuredData={breadcrumbStructuredData}
      />
      
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`} aria-label="Main navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
            <div className="hidden md:flex items-center gap-6">
              <a href="tel:18884589181" className={`flex items-center gap-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>
                <i className="ri-phone-line"></i>1-888-458-9181
              </a>
              <Link to="/camera-packages" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>Cameras</Link>
              <Link to="/access-control" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>Access Control</Link>
              <Link to="/alarm-packages" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>Alarm Packages</Link>
              <Link to="/live-monitoring" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>Live Monitoring</Link>
              <Link to="/clients" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-[#D4A853]' : 'text-white'}`}>Clients</Link>
              <Link to="/faq" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>FAQ</Link>
              <div
                className="relative"
                onMouseEnter={() => setServiceAreasOpen(true)}
                onMouseLeave={() => setServiceAreasOpen(false)}
              >
                <button className={`text-sm font-medium transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-[#D4A853]' : 'text-white/90 hover:text-white'}`}>
                  Service Areas
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${serviceAreasOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {serviceAreasOpen && (
                  <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                    {serviceAreas.map((area) => (
                      <Link key={area.path} to={area.path} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#D4A853]/10 hover:text-[#D4A853] transition-colors cursor-pointer">{area.name}</Link>
                    ))}
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <Link to="/service-areas" className="block px-4 py-2.5 text-sm font-semibold text-[#D4A853] hover:bg-[#D4A853]/10 transition-colors cursor-pointer">View All Areas →</Link>
                    </div>
                  </div>
                )}
              </div>
              <Link to="/#contact" className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap">Get Free Quote</Link>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className={`md:hidden ${isScrolled ? 'bg-white' : 'bg-[#0A1628]/95 backdrop-blur-sm'} border-t ${isScrolled ? 'border-gray-100' : 'border-white/10'} px-4 pb-4 space-y-2`}>
              <Link to="/camera-packages" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>Camera Packages</Link>
              <Link to="/access-control" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>Access Control</Link>
              <Link to="/alarm-packages" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>Alarm Packages</Link>
              <Link to="/live-monitoring" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>Live Monitoring</Link>
              <Link to="/clients" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-[#D4A853] border-gray-50' : 'text-[#D4A853] border-white/10'}`}>Clients</Link>
              <Link to="/faq" className={`block py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}>FAQ</Link>
              <div className={`border-b ${isScrolled ? 'border-gray-50' : 'border-white/10'}`}>
                <button
                  onClick={() => setMobileServiceAreasOpen(!mobileServiceAreasOpen)}
                  className={`w-full flex items-center justify-between py-3 text-sm font-medium cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-white/90'}`}
                >
                  Service Areas
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${mobileServiceAreasOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileServiceAreasOpen && (
                  <div className="pl-4 pb-3 space-y-1">
                    {serviceAreas.map((area) => (
                      <Link key={area.path} to={area.path} className={`block py-2 text-sm transition-colors cursor-pointer ${isScrolled ? 'text-gray-600 hover:text-[#D4A853]' : 'text-white/70 hover:text-white'}`}>{area.name}</Link>
                    ))}
                    <Link to="/service-areas" className="block py-2 text-sm font-semibold text-[#D4A853] hover:text-[#B8923D] transition-colors cursor-pointer">View All Areas →</Link>
                  </div>
                )}
              </div>
              <a href="tel:18884589181" className={`flex items-center gap-2 py-3 text-sm font-medium border-b ${isScrolled ? 'text-gray-700 border-gray-50' : 'text-white/90 border-white/10'}`}><i className="ri-phone-line"></i>1-888-458-9181</a>
              <Link to="/#contact" className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2">Get Free Quote</Link>
            </div>
          )}
        </nav>

        {/* Hero Section */}
        <main>
          <section className="relative min-h-[50vh] sm:min-h-[65vh] flex items-center pt-16 sm:pt-20" aria-labelledby="clients-hero-heading">
            <div className="absolute inset-0">
              <img
                src="https://public.readdy.ai/ai/img_res/8b669a6f9cadd349bf0e47676be588f7.jpg"
                alt="Our Clients"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/35"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full py-12 sm:py-20">
              <div className="max-w-3xl">
                <span className="inline-block bg-[#D4A853] text-white px-5 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 whitespace-nowrap">
                  TRUSTED BY INDUSTRY LEADERS
                </span>
                <h1 id="clients-hero-heading" className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 sm:mb-6 leading-tight">
                  <strong>Our Valued Clients</strong>
                </h1>
                <p className="text-base sm:text-xl text-white/90 mb-6 sm:mb-8 max-w-2xl">
                  Protecting over 30,000 businesses and homes across the Greater Toronto Area since 1999. From local shops to national brands, we deliver security solutions you can count on.
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 sm:gap-4">
                  <Link
                    to="/#contact"
                    className="w-full sm:w-auto bg-[#D4A853] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg"
                  >
                    Become a Client
                    <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                      <i className="ri-arrow-right-line text-lg"></i>
                    </span>
                  </Link>
                  <a
                    href="tel:18884589181"
                    className="w-full sm:w-auto border-2 border-white text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer text-center"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    1-888-458-9181
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Client Logos Grid */}
          <section className="py-12 sm:py-24 bg-white" aria-labelledby="clients-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-8 sm:mb-12">
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 whitespace-nowrap">
                  CLIENT PORTFOLIO
                </span>
                <h2 id="client-portfolio-heading" className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                  Businesses That Trust Us
                </h2>
                <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                  From national brands to local businesses, we provide reliable security across every industry
                </p>
              </div>

              {/* Industry Filter */}
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12">
                {industries.map((industry) => (
                  <button
                    key={industry}
                    onClick={() => setActiveFilter(industry)}
                    className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeFilter === industry
                        ? 'bg-[#D4A853] text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-[#D4A853]/10 hover:text-[#D4A853]'
                    }`}
                  >
                    {industry}
                  </button>
                ))}
              </div>

              {/* Client Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 md:gap-6">
                {filteredClients.map((client, index) => (
                  <div
                    key={index}
                    className="bg-[#F8F9FA] rounded-xl p-3 sm:p-4 md:p-6 shadow-md hover:shadow-lg transition-all duration-300 flex flex-col items-center justify-center h-24 sm:h-28 md:h-32 border border-gray-100 hover:border-[#D4A853]/30 group"
                  >
                    <img
                      src={client.image}
                      alt={client.name}
                      title={client.name}
                      className="max-w-full max-h-[60%] object-contain grayscale group-hover:grayscale-0 transition-all duration-300 scale-110 sm:scale-125"
                    />
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-2 text-center truncate w-full group-hover:text-[#D4A853] transition-colors">{client.name}</p>
                  </div>
                ))}
              </div>

              {filteredClients.length === 0 && (
                <div className="text-center py-12">
                  <i className="ri-search-line text-4xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">No clients found in this category.</p>
                </div>
              )}
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-12 sm:py-16 md:py-24 bg-[#F8F9FA]" aria-labelledby="testimonials-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-10 sm:mb-16">
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 whitespace-nowrap">
                  CLIENT FEEDBACK
                </span>
                <h2 id="testimonials-heading" className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                  What Our Clients Say
                </h2>
                <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                  Hear directly from the businesses we protect every day
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-8">
                {testimonials.map((t, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <i className="ri-double-quotes-l text-4xl sm:text-5xl text-[#D4A853]/30 mb-4 block"></i>
                    <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed">"{t.quote}"</p>
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-sm font-bold text-[#0A1628]">{t.name}</p>
                      <p className="text-xs text-gray-500">{t.company}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Why Businesses Choose Us */}
          <section className="py-12 sm:py-16 md:py-24 bg-white" aria-labelledby="why-us-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-10 sm:mb-16">
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 whitespace-nowrap">
                  WHY US
                </span>
                <h2 id="why-us-heading" className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                  Why Businesses Choose Us
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
                {[
                  { icon: 'ri-shield-star-line', title: '25+ Years Experience', desc: 'Trusted since 1999 with over 30,000 successful installations across the GTA.' },
                  { icon: 'ri-customer-service-2-line', title: '24/7 Support', desc: 'Round-the-clock monitoring and technical support for complete peace of mind.' },
                  { icon: 'ri-award-line', title: 'A+ BBB Rating', desc: 'Consistently rated A+ by the Better Business Bureau for quality and reliability.' },
                  { icon: 'ri-tools-line', title: 'Custom Solutions', desc: 'Tailored security systems designed specifically for your business needs and budget.' },
                ].map((item, index) => (
                  <div key={index} className="text-center p-6 sm:p-8">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#D4A853] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                      <i className={`${item.icon} text-2xl sm:text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-3">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-12 sm:py-24 bg-[#0A1628]" aria-labelledby="cta-heading">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
              <h2 id="cta-heading" className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">
                Ready to Join Our Growing Client List?
              </h2>
              <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8">
                Get a free security assessment and see why thousands of businesses trust us
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4">
                <Link
                  to="/#contact"
                  className="w-full sm:w-auto bg-[#D4A853] text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap text-center"
                >
                  Schedule Free Consultation
                </Link>
                <a
                  href="tel:18884589181"
                  className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap text-center"
                >
                  1-888-458-9181
                </a>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-[#050B14] text-white py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
              <div className="flex items-center gap-3">
                <div className="flex flex-col leading-none">
                  <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                  <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
                </div>
                <span className="text-white/60 text-xs sm:text-sm">Trusted Security Since 1999</span>
              </div>
              <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
                <Link to="/" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Home</Link>
                <Link to="/clients" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Clients</Link>
                <Link to="/service-areas" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Service Areas</Link>
                <Link to="/privacy-policy" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Privacy Policy</Link>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
